/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiLink } from '@elastic/eui'

import type { AnyObservabilityProject } from '@modules/project-api/types'
import genericMessages from '@modules/project-lib/genericMessages'
import { FormattedMessage } from '@modules/i18n'
import usePermissions from '@modules/permissions-lib/hooks'

import ObservabilityProjectFeaturesModal from './ObservabilityProjectFeaturesModal'

import type { AnyObservabilityProjectProductTier } from './types'
import type { MessageDescriptor } from 'react-intl'

export type Props = {
  project: AnyObservabilityProject
  onChange: (productTypes: AnyObservabilityProject['product_tier'], onSettled: () => void) => void
  isLoading: boolean
}

const ObservabilityProjectFeatures: React.FunctionComponent<Props> = ({
  project,
  onChange,
  isLoading,
}) => {
  const productTierMapping: Record<AnyObservabilityProjectProductTier, MessageDescriptor> = {
    complete: genericMessages.observabilityComplete,
    logs_essentials: genericMessages.observabilityLogsEssentials,
  }

  const tierNameToDisplay = project.product_tier
    ? productTierMapping[project.product_tier]
    : genericMessages.complete

  const { hasPermissions: hasUpdatePermission } = usePermissions([
    { type: `project-${project.type}`, action: 'update', id: project.id },
  ])

  const [isEditing, setIsEditing] = useState(false)

  return (
    <div data-test-subj='editProjectFeatures'>
      <EuiFlexGroup
        responsive={false}
        gutterSize='m'
        alignItems='center'
        css={{ whiteSpace: 'nowrap' }}
      >
        <EuiFlexItem grow={true} data-test-subj='currentValue'>
          <FormattedMessage {...tierNameToDisplay} />
        </EuiFlexItem>
        {hasUpdatePermission && (
          <EuiFlexItem grow={false}>
            <EuiLink onClick={() => setIsEditing(true)}>
              <FormattedMessage {...genericMessages.edit} />
            </EuiLink>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
      {isEditing && (
        <ObservabilityProjectFeaturesModal
          project={project}
          onClose={() => setIsEditing(false)}
          onChangeSave={onChange}
          isLoading={isLoading}
        />
      )}
    </div>
  )
}

export default ObservabilityProjectFeatures
